import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import StatusPill from '../../components/StatusPill';
import { Card, EmptyState, LoadingRows, Notice, PageHeader, SearchInput, Segmented } from '../../components/ui/Page';
import { date } from '../../lib/format';

export type Partner = {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  firm: string | null;
  focus: string | null;
  capacity: string | null;
  status: 'INVITED' | 'ACTIVE' | 'PAUSED';
  createdAt: string;
  createdBy: { name: string | null; email: string } | null;
};

export const PARTNER_TONE = { ACTIVE: 'green', INVITED: 'amber', PAUSED: 'slate' } as const;

export default function PartnersPage() {
  const navigate = useNavigate();
  const { data, loading, error } = useApi<{ partners: Partner[] }>('/admin/partners');
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<'all' | Partner['status']>('all');
  const partners = data?.partners || [];

  const shown = useMemo(() => {
    const term = q.trim().toLowerCase();
    return partners.filter(
      (p) => (status === 'all' || p.status === status) && (!term || [p.name, p.email, p.firm, p.focus].some((v) => v?.toLowerCase().includes(term))),
    );
  }, [partners, q, status]);

  const count = (s: Partner['status']) => partners.filter((p) => p.status === s).length;

  return (
    <div>
      <PageHeader
        title="Partners"
        description="Funding partners that receive rated companies from Consult. Partners are added only from this console."
        actions={
          <Link to="/partners/new" className="mz-btn-primary">
            + Add partner
          </Link>
        }
      />
      <Card className="mz-fade-up" padded={false}>
        <div className="flex flex-wrap items-center gap-2 border-b border-[#fbeef4] p-3">
          <SearchInput value={q} onChange={setQ} placeholder="Search name, firm or focus…" className="min-w-[220px] flex-1" />
          <Segmented
            value={status}
            onChange={setStatus}
            options={[
              { value: 'all', label: 'All', count: partners.length },
              { value: 'ACTIVE', label: 'Active', count: count('ACTIVE') },
              { value: 'INVITED', label: 'Invited', count: count('INVITED') },
              { value: 'PAUSED', label: 'Paused', count: count('PAUSED') },
            ]}
          />
        </div>
        {error && (
          <div className="px-3 pt-3">
            <Notice tone="error">{error}</Notice>
          </div>
        )}
        {loading && !data ? (
          <LoadingRows />
        ) : shown.length === 0 ? (
          <EmptyState
            title={partners.length ? 'No partners match' : 'No partners yet'}
            description={partners.length ? undefined : 'Add lenders and investors so analysts can share rated companies with them.'}
            action={!partners.length && <Link to="/partners/new" className="mz-btn-primary">+ Add partner</Link>}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="mz-table min-w-[720px]">
              <thead>
                <tr>
                  <th>Partner</th>
                  <th>Firm</th>
                  <th>Focus</th>
                  <th>Ticket size</th>
                  <th>Added</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((p) => (
                  <tr key={p.id} className="mz-row-link" onClick={() => navigate(`/partners/${p.id}`)}>
                    <td>
                      <p className="font-semibold">{p.name}</p>
                      <p className="text-xs text-[#a77d92]">{p.email}</p>
                    </td>
                    <td className="text-[13px]">{p.firm || <span className="text-[#c9a3b5]">Independent</span>}</td>
                    <td className="max-w-[200px] truncate text-[13px] text-[#9a6b82]">{p.focus || '—'}</td>
                    <td className="text-[13px] text-[#9a6b82]">{p.capacity || '—'}</td>
                    <td className="whitespace-nowrap text-xs text-[#9a6b82]">{date(p.createdAt)}</td>
                    <td>
                      <StatusPill label={p.status} tone={PARTNER_TONE[p.status]} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
